import { useCallback, useEffect, useState } from 'react'
import type { Abi } from 'viem'
import { useAccount } from 'wagmi'

import { publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

export type OwnerType = 'eoa' | 'multisig' | null

const SAFE_ABI: Abi = [
  {
    inputs: [],
    name: 'getOwners',
    outputs: [{ internalType: 'address[]', name: '', type: 'address' }],
    stateMutability: 'view',
    type: 'function',
  },
  {
    inputs: [{ internalType: 'address', name: 'owner', type: 'address' }],
    name: 'isOwner',
    outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
    stateMutability: 'view',
    type: 'function',
  },
]

export const useMultisigOwner = ({ contract }: { contract: any }) => {
  const { address } = useAccount()

  const [isOwner, setIsOwner] = useState(false)
  const [isMultisigOwner, setIsMultisigOwner] = useState(false)
  const [ownerType, setOwnerType] = useState<OwnerType>(null)
  const [ownerAddress, setOwnerAddress] = useState<string | null>(null)

  const checkOwner = useCallback(async () => {
    if (!contract || !address) {
      setIsOwner(false)
      setIsMultisigOwner(false)
      setOwnerType(null)
      setOwnerAddress(null)
      return
    }

    try {
      const owner: string = await contract.read.owner()
      setOwnerAddress(owner)

      if (owner?.toLowerCase() === address.toLowerCase()) {
        setIsOwner(true)
        setIsMultisigOwner(false)
        setOwnerType('eoa')
        return
      }

      try {
        const isSafeOwner = (await publicWallet.readContract({
          address: owner as `0x${string}`,
          abi: SAFE_ABI,
          functionName: 'isOwner',
          args: [address],
        })) as boolean

        setIsOwner(Boolean(isSafeOwner))
        setIsMultisigOwner(Boolean(isSafeOwner))
        setOwnerType('multisig')
      } catch {
        setIsOwner(false)
        setIsMultisigOwner(false)
        setOwnerType('eoa')
      }
    } catch (error: any) {
      setIsOwner(false)
      setIsMultisigOwner(false)
      setOwnerType(null)
      setOwnerAddress(null)
      logError(error)
    }
  }, [address, contract])

  useEffect(() => {
    checkOwner()
  }, [address, contract, checkOwner])

  return {
    isOwner,
    isMultisigOwner,
    ownerType,
    ownerAddress,
    checkOwner,
  }
}
